import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  HomeWork as HomeWorkIcon,
  Image as ImageIcon,
  AspectRatio as AspectRatioIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import {ImageComparison} from './ImageComparison';

interface Shot {
  id: string;
  shotType: string;
  shotStatus: string;
  roomType: string;
  frameScore: number;
  sourceClipIndex: number;
  sourceFramePosition: string;
  originalImageURI: string;
  enhancedImageURI: string | null;
  stagedImageURI: string | null;
  createdAt: string;
  errorMessage: string | null;
}

interface ShotDetailModalProps {
  shot: Shot | null;
  open: boolean;
  onClose: () => void;
  onRegenerateShot: (shotId: string) => void;
  onGenerateStaged: (shotId: string) => void;
}

export function ShotDetailModal({
  shot,
  open,
  onClose,
  onRegenerateShot,
  onGenerateStaged,
}: ShotDetailModalProps) {
  const [fitMode, setFitMode] = React.useState<'contain' | 'cover'>('cover');

  if (!shot) return null;

  const downloadImage = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Determine which images to show
  const leftImage = shot.originalImageURI;
  const rightImage =
    shot.stagedImageURI || shot.enhancedImageURI || shot.originalImageURI;
  const showComparison = shot.enhancedImageURI || shot.stagedImageURI;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#000',
        },
      }}
    >
      <AppBar position="relative" color="transparent" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{flex: 1, color: 'white'}}>
            {shot.roomType} - Clip {shot.sourceClipIndex} (
            {shot.sourceFramePosition})
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Score: ${shot.frameScore}`}
              size="small"
              color="primary"
              sx={{color: 'white'}}
            />
            <Chip
              label={shot.shotStatus}
              size="small"
              color={
                shot.shotStatus === 'COMPLETED'
                  ? 'success'
                  : shot.shotStatus === 'PROCESSING'
                  ? 'info'
                  : shot.shotStatus === 'FAILED'
                  ? 'error'
                  : 'default'
              }
            />

            {/* Action Buttons */}
            {shot.shotStatus !== 'PROCESSING' && (
              <>
                {!shot.enhancedImageURI && (
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => onRegenerateShot(shot.id)}
                    variant="outlined"
                    sx={{color: 'white', borderColor: 'white'}}
                  >
                    Enhance
                  </Button>
                )}

                {shot.enhancedImageURI && (
                  <IconButton
                    size="small"
                    onClick={() => onRegenerateShot(shot.id)}
                    sx={{color: 'white'}}
                  >
                    <RefreshIcon />
                  </IconButton>
                )}

                {shot.enhancedImageURI && !shot.stagedImageURI && (
                  <Button
                    size="small"
                    startIcon={<HomeWorkIcon />}
                    onClick={() => onGenerateStaged(shot.id)}
                    variant="outlined"
                    sx={{color: 'white', borderColor: 'white'}}
                  >
                    Stage
                  </Button>
                )}
              </>
            )}

            {/* View Mode Toggle */}
            <IconButton
              onClick={() =>
                setFitMode(fitMode === 'contain' ? 'cover' : 'contain')
              }
              sx={{color: 'white'}}
              title={fitMode === 'contain' ? 'Fill screen' : 'Fit to screen'}
            >
              {fitMode === 'contain' ? <FullscreenIcon /> : <AspectRatioIcon />}
            </IconButton>

            {/* Download Buttons */}
            <IconButton
              onClick={() =>
                downloadImage(
                  shot.originalImageURI,
                  `${shot.roomType}_original_${shot.id}.jpg`
                )
              }
              sx={{color: 'white'}}
              title="Download original"
            >
              <ImageIcon />
            </IconButton>

            {shot.enhancedImageURI && (
              <IconButton
                onClick={() =>
                  downloadImage(
                    shot.enhancedImageURI,
                    `${shot.roomType}_enhanced_${shot.id}.jpg`
                  )
                }
                sx={{color: 'white'}}
                title="Download enhanced"
              >
                <DownloadIcon />
              </IconButton>
            )}

            {shot.stagedImageURI && (
              <IconButton
                onClick={() =>
                  downloadImage(
                    shot.stagedImageURI,
                    `${shot.roomType}_staged_${shot.id}.jpg`
                  )
                }
                sx={{color: 'white'}}
                title="Download staged"
              >
                <DownloadIcon color="secondary" />
              </IconButton>
            )}

            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
              sx={{color: 'white'}}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#1a1a1a',
          }}
        >
          {showComparison ? (
            <ImageComparison
              leftImage={leftImage}
              rightImage={rightImage}
              leftLabel="Original"
              rightLabel={shot.stagedImageURI ? 'Staged' : 'Enhanced'}
              fitMode={fitMode}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={shot.originalImageURI}
                alt={shot.roomType}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </Box>

        {shot.errorMessage && (
          <Box sx={{p: 2, backgroundColor: 'error.dark'}}>
            <Typography color="error.contrastText" variant="body2">
              Error: {shot.errorMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
